import { DrawerSlotsAndSlotProps } from '@mui/joy';
import { useMemo } from 'react';
import { FieldValues, UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { Form } from 'core/components';
import { AccessPermissions } from 'core/enums';
import { FormProvider, useNotificationContext } from 'core/states';
import { attributesDrawerWidth } from 'quotes/components/ExportSkuPart/ExportSkuPartSteps';
import { AttributeFormFields } from 'quotes/components/ExportSkuPart/steps/Attributes/AttributeFormFields';
import {
  getPriorityModeFromConfig,
  mapAttributesToForm,
} from 'quotes/components/ExportSkuPart/steps/Attributes/mappers';
import { attributeSchema } from 'quotes/components/ExportSkuPart/steps/Attributes/schema';
import { IAttributeForm } from 'quotes/components/ExportSkuPart/types';
import { VendorQuoteHelper } from 'quotes/helpers';
import { useSaveAttributes } from 'quotes/hooks';
import { IAttributes } from 'quotes/types';

import { mapFormToSavePayload } from './mappers';

const editDrawerSlotAndSlotProps: DrawerSlotsAndSlotProps = {
  slotProps: { content: { sx: { width: attributesDrawerWidth } } },
};

interface IProps {
  quoteId: number;
  attributes: IAttributes | null;
  // Bounds the Trade Agreement Start Date; absent means the quote never expires.
  validUntil?: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const EditAttributesFormFields = ({ quoteId, attributes, validUntil, isOpen, onClose }: IProps) => {
  const { t } = useTranslation();

  const { hasPermission } = useUserPermissions();
  const canChooseSpaStatus = hasPermission(AccessPermissions.ALLOW_LIVE_OR_PLANNING_SPA);
  const canEnableSelectableSpa = hasPermission(AccessPermissions.SELECTABLE_SPA);

  const { saveAttributes, isSaving } = useSaveAttributes();
  const { clearNotification } = useNotificationContext();

  const hydratedPriorityMode = useMemo(() => getPriorityModeFromConfig(attributes), [attributes]);

  const onSubmit = (reset: UseFormReset<FieldValues>) => async (data: IAttributeForm) => {
    try {
      const { success } = await saveAttributes(
        mapFormToSavePayload(quoteId, data, { canChooseSpaStatus, canEnableSelectableSpa })
      );

      if (!success) {
        return;
      }
    } catch {
      return;
    }

    setTimeout(() => handleOnClose(reset), 1000);
  };

  const handleOnClose = (reset: UseFormReset<FieldValues>) => {
    // The FORM notification lives in shared context; the drawer unmounts its own
    // notification slot on close before the 5s auto-close fires, so clear it here
    // to avoid a stale banner flashing on the next open (mirrors UpsertCorporatePartPrice).
    clearNotification();
    reset();
    onClose();
  };

  return (
    <Form
      title={t('titleAttributes')}
      isOpen={isOpen}
      onClose={handleOnClose}
      onSubmit={onSubmit}
      submitBtnLabel={t('buttonLabelSave')}
      cancelBtnLabel={t('buttonLabelCancel')}
      disabledSubmit={isSaving}
      drawerSlotAndSlotProps={editDrawerSlotAndSlotProps}
    >
      <AttributeFormFields hydratedPriorityMode={hydratedPriorityMode} validUntil={validUntil} />
    </Form>
  );
};

export const EditAttributes = (props: IProps) => {
  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();
  const defaultValues = useMemo(() => mapAttributesToForm(props.attributes), [props.attributes]);

  return (
    <FormProvider
      schema={attributeSchema(
        props.validUntil,
        VendorQuoteHelper.getSpaAudienceRights(user),
        hasPermission(AccessPermissions.ALLOW_LIVE_OR_PLANNING_SPA)
      )}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <EditAttributesFormFields {...props} />
    </FormProvider>
  );
};
